import { useState, useEffect, useMemo, useCallback } from 'react';
import { LuChartColumn, LuMonitor, LuUser, LuUsers } from 'react-icons/lu';
import PageHeader, { type PageTab } from '../components/PageHeader';
import Select, { iniciaisDe } from '../components/Select';
import IndividualTab, { type GoalsTimeframe } from '../components/IndividualTab';
import AnalyticsPage from './AnalyticsPage';
import TeamsTab from '../components/TeamsTab';
import MonthPicker from '../components/MonthPicker';
import RelatorioService from '../services/RelatorioService';
import type {
  PerformanceIndividual, PerformanceEquipe, PerformanceEquipeMembro, ProjecaoFechamento,
} from '../services/RelatorioService';
import { OpportunityService, type Opportunity } from '../services/OpportunityService';
import EquipeService, { type Equipe } from '../services/EquipeService';
import GerenciarEmpresaService from '../services/GerenciarEmpresaService';
import type { DiasUteis } from '../services/GerenciarEmpresaService';
import { getUserPermissions, PERMISSOES } from '../config/permissions';
import './ChatDashboard.css';

const DEFAULT_WORKING_DAYS: DiasUteis = {
  segunda: true, terca: true, quarta: true, quinta: true, sexta: true, sabado: false, domingo: false,
};

// dayNames indexed by getDay(): 0=Sun,1=Mon,...,6=Sat
const DAY_NAMES: (keyof DiasUteis)[] = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];

const ChatDashboard = () => {
  const userPermissions = useMemo(() => getUserPermissions(), []);
  const isAdmin = userPermissions.includes(PERMISSOES.gestaoVendas);
  const defaultTab = isAdmin ? 'analytics' : 'monitoring';
  const [activeTab, setActiveTab] = useState<'analytics' | 'monitoring' | 'teams'>(defaultTab);
  const [workingDays, setWorkingDays] = useState<DiasUteis>(DEFAULT_WORKING_DAYS);
  const [goalsTimeframe, setGoalsTimeframe] = useState<GoalsTimeframe>('month');
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);

  const currentMonthStr = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const [monitoringMonth, setMonitoringMonth] = useState(currentMonthStr);

  const [individualPerf, setIndividualPerf] = useState<PerformanceIndividual[]>([]);
  const [teamPerf, setTeamPerf] = useState<PerformanceEquipe[]>([]);
  const [teamMembers, setTeamMembers] = useState<PerformanceEquipeMembro[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [isLoadingIndividual, setIsLoadingIndividual] = useState(false);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);

  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [teams, setTeams] = useState<Equipe[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
  const [selectedAgentProjecao, setSelectedAgentProjecao] = useState<ProjecaoFechamento | null>(null);

  const workingDaysInfo = useMemo(() => {
    const d = new Date();
    const year = d.getFullYear();
    const month = d.getMonth();
    let monthlyDays = 0;
    let elapsedDays = 0;
    const today = d.getDate();
    const tempDate = new Date(year, month, 1);
    while (tempDate.getMonth() === month) {
      if (workingDays[DAY_NAMES[tempDate.getDay()]]) {
        monthlyDays++;
        if (tempDate.getDate() <= today) elapsedDays++;
      }
      tempDate.setDate(tempDate.getDate() + 1);
    }
    const weeklyDays = Object.values(workingDays).filter(Boolean).length;
    return {
      monthlyDays: monthlyDays || 22,
      weeklyDays: weeklyDays || 5,
      elapsedDays: elapsedDays || 1,
    };
  }, [workingDays]);

  useEffect(() => {
    GerenciarEmpresaService.getDiasUteis()
      .then(setWorkingDays)
      .catch(() => {/* keep default */ });
    OpportunityService.getAllOpportunities()
      .then(setOpportunities)
      .catch(() => { });
    EquipeService.buscarTodas()
      .then(setTeams)
      .catch(() => { });
  }, []);

  const fetchIndividual = useCallback(async (mes: string) => {
    setIsLoadingIndividual(true);
    try {
      const data = await RelatorioService.getPerformanceIndividual(mes);
      setIndividualPerf(data);
    } catch {
      setIndividualPerf([]);
    } finally {
      setIsLoadingIndividual(false);
    }
  }, []);

  const fetchTeams = useCallback(async (mes: string) => {
    setIsLoadingTeams(true);
    try {
      const data = await RelatorioService.getPerformanceEquipes(mes);
      setTeamPerf(data);
    } catch {
      setTeamPerf([]);
    } finally {
      setIsLoadingTeams(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'monitoring') fetchIndividual(monitoringMonth);
    if (activeTab === 'teams') fetchTeams(monitoringMonth);
  }, [activeTab, monitoringMonth, fetchIndividual, fetchTeams]);

  const loggedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}') as { id?: number; nome?: string };
    } catch {
      return {} as { id?: number; nome?: string };
    }
  }, []);

  const rankedForPicker = useMemo(
    () => [...individualPerf].sort((a, b) => b.percentualFaturamento - a.percentualFaturamento),
    [individualPerf]);

  /* Gestor escolhe a pessoa e abre no primeiro do ranking; vendedor não
     escolhe — é sempre ele, e a aba se chama "Meu desempenho". */
  useEffect(() => {
    if (rankedForPicker.length === 0) {
      setSelectedAgentId(null);
      return;
    }
    if (!isAdmin) {
      const me = rankedForPicker.find(a => a.usuarioId === loggedUser.id)
        ?? rankedForPicker.find(a => a.usuarioNome === loggedUser.nome);
      setSelectedAgentId(me?.usuarioId ?? null);
      return;
    }
    setSelectedAgentId(prev =>
      prev !== null && rankedForPicker.some(a => a.usuarioId === prev)
        ? prev
        : rankedForPicker[0].usuarioId);
  }, [rankedForPicker, isAdmin, loggedUser.id, loggedUser.nome]);

  const selectedAgent = useMemo(
    () => rankedForPicker.find(a => a.usuarioId === selectedAgentId) ?? null,
    [rankedForPicker, selectedAgentId]);

  useEffect(() => {
    if (activeTab !== 'monitoring' || !selectedAgent) {
      setSelectedAgentProjecao(null);
      return;
    }
    let cancelled = false;
    setSelectedAgentProjecao(null);
    RelatorioService.getProjecaoFechamento(monitoringMonth, selectedAgent.usuarioId)
      .then(p => { if (!cancelled) setSelectedAgentProjecao(p); })
      .catch(() => { /* o IndividualTab cai no cálculo local */ });
    return () => { cancelled = true; };
  }, [activeTab, monitoringMonth, selectedAgent]);

  /* A equipe em foco: abre na de maior atingimento, e cai para ela de volta se
     a escolhida sumir ao trocar de mês. */
  const rankedTeams = useMemo(
    () => [...teamPerf].sort((a, b) => b.percentualAtingido - a.percentualAtingido),
    [teamPerf]);

  useEffect(() => {
    if (rankedTeams.length === 0) {
      setSelectedTeamId(null);
      return;
    }
    setSelectedTeamId(prev =>
      prev !== null && rankedTeams.some(t => t.equipeId === prev)
        ? prev
        : rankedTeams[0].equipeId);
  }, [rankedTeams]);

  useEffect(() => {
    if (activeTab !== 'teams' || selectedTeamId === null) {
      setTeamMembers([]);
      return;
    }
    let cancelled = false;
    setIsLoadingMembers(true);
    RelatorioService.getPerformanceEquipeMembros(selectedTeamId, monitoringMonth)
      .then(d => { if (!cancelled) setTeamMembers(d.membros); })
      .catch(() => { if (!cancelled) setTeamMembers([]); })
      .finally(() => { if (!cancelled) setIsLoadingMembers(false); });
    return () => { cancelled = true; };
  }, [activeTab, selectedTeamId, monitoringMonth]);

  /* Clicar num membro leva para o dossiê dele na aba Individual. */
  const handleMemberClick = useCallback((usuarioId: number) => {
    setSelectedAgentId(usuarioId);
    setActiveTab('monitoring');
  }, []);

  // ── Individual aggregates ─────────────────────────────────────
  // ── Teams aggregates ──────────────────────────────────────────
  const selectedTeam = useMemo(() =>
    selectedTeamId !== null ? teamPerf.find(t => t.equipeId === selectedTeamId) ?? null : null,
    [selectedTeamId, teamPerf]);

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

  /* O gestor escolhe a pessoa; o vendedor vê a si mesmo — mesma aba, rótulo
     diferente. A antiga "Meu desempenho" era uma segunda cópia disso. */
  const tabs: PageTab[] = [
    ...(isAdmin ? [
      { id: 'analytics', label: 'Analytics', icon: <LuChartColumn /> },
      { id: 'monitoring', label: 'Individual', icon: <LuMonitor /> },
    ] : [
      { id: 'monitoring', label: 'Meu desempenho', icon: <LuUser /> },
    ]),
    { id: 'teams', label: 'Equipes', icon: <LuUsers /> },
  ];

  return (
    <div className="page-shell chat-dashboard commercial-dashboard">
      {/* O SellerMonitoringModal saiu: o conteúdo dele virou a aba Individual.
          O arquivo continua no repo, sem uso, até o ok para apagar. */}

      <PageHeader
        title="Gestão de vendas"
        subtitle="Metas, ritmo e projeção do time comercial"
        actions={
          <>
            {activeTab === 'teams' && rankedTeams.length > 0 && (
              <Select
                size="control"
                block={false}
                showOptionAccessory
                options={rankedTeams.map(t => ({
                  value: String(t.equipeId),
                  label: t.equipeNome,
                  avatar: iniciaisDe(t.equipeNome),
                  meta: `${formatCurrency(t.totalFaturado)} · ${Math.round(t.percentualAtingido)}%`,
                }))}
                value={selectedTeamId ? String(selectedTeamId) : ''}
                onChange={v => setSelectedTeamId(Number(v))}
                placeholder="Escolher equipe"
              />
            )}
            {activeTab === 'monitoring' && isAdmin && rankedForPicker.length > 0 && (
              <Select
                size="control"
                block={false}
                showOptionAccessory
                options={rankedForPicker.map(a => ({
                  value: String(a.usuarioId),
                  label: a.usuarioNome,
                  avatar: iniciaisDe(a.usuarioNome),
                  meta: a.metaFaturamento > 0
                    ? `${formatCurrency(a.totalFaturado)} · ${Math.round(a.percentualFaturamento)}%`
                    : formatCurrency(a.totalFaturado),
                }))}
                value={selectedAgentId ? String(selectedAgentId) : ''}
                onChange={v => setSelectedAgentId(Number(v))}
                placeholder="Escolher vendedor"
              />
            )}
            <MonthPicker value={monitoringMonth} onChange={setMonitoringMonth} />
          </>
        }
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={id => setActiveTab(id as typeof activeTab)}
      />

      <div className="page-body">

      {/* ── ANALYTICS ──────────────────────────────────────────── */}
      {activeTab === 'analytics' && isAdmin && (
        <AnalyticsPage selectedMonth={monitoringMonth} />
      )}

      {/* ── INDIVIDUAL ─────────────────────────────────────────── */}
      {activeTab === 'monitoring' && (
        isLoadingIndividual ? (
          <div className="dashboard-card">
            <span className="skeleton skeleton-text-md" style={{ width: '220px', marginBottom: '20px' }} />
            <span className="skeleton" style={{ height: '40px', width: '260px', borderRadius: '8px', display: 'block', marginBottom: '18px' }} />
            <span className="skeleton" style={{ height: '14px', borderRadius: '4px', display: 'block' }} />
          </div>
        ) : (
          <IndividualTab
            agent={selectedAgent}
            projecao={selectedAgentProjecao}
            monitoringMonth={monitoringMonth}
            opportunities={opportunities}
            individualPerf={individualPerf}
            teams={teams}
            workingDaysInfo={workingDaysInfo}
            timeframe={goalsTimeframe}
            onTimeframeChange={setGoalsTimeframe}
          />
        )
      )}

      {/* ── EQUIPES ────────────────────────────────────────────── */}
      {activeTab === 'teams' && (
        <TeamsTab
          team={selectedTeam}
          members={teamMembers}
          isLoadingMembers={isLoadingTeams || isLoadingMembers}
          opportunities={opportunities}
          workingDaysInfo={workingDaysInfo}
          timeframe={goalsTimeframe}
          onTimeframeChange={setGoalsTimeframe}
          onMemberClick={handleMemberClick}
        />
      )}
      </div>
    </div>
  );
};

export default ChatDashboard;
